import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormProps, Option } from '../../types/form.type';
import { Router } from '@angular/router';
import { ApiService } from '@hospitality-bot/shared/utils';
import { Subscription } from 'rxjs';
import { AdminUtilityService } from '../../services/admin-utility.service';
import { ControlContainer } from '@angular/forms';
import { FormComponent } from '../../components/form-component/form.components';
import { MultiSelectSettings } from '../../components/form-component/multi-select/multi-select.component';
import { SnackBarService } from '@hospitality-bot/shared/material';
import { convertToTitleCase } from '../../utils/valueFormatter';
import { getUniqueOptions } from '../../utils/shared';

@Component({
  selector: 'hospitality-bot-quick-select',
  templateUrl: './quick-select.component.html',
  styleUrls: ['./quick-select.component.scss'],
})
export class QuickSelectComponent extends FormComponent implements OnInit {
  /**
   * Initial Config Variables
   */
  _qsProps: QSProps;
  postLink: string; // api endpoint , modal also

  placeholder: string;
  class:
    | 'half-width'
    | 'full-width'
    | 'one-half-width'
    | 'one-third-width'
    | 'one-fourth-width';
  isAsync = false;
  qsLoading = false;
  resetApiData = false;

  /** For handling data after api call */
  emitResponse = false;

  /**
   * Pagination Variables
   */
  isPagination: boolean;
  offSet = 0;
  noMoreData = true;
  limit = 10;
  searchLimit = 10;

  /**
   * @var dataModel API model for requesting OptionList
   * @type key is optional
   */
  model = {
    key: 'records',
    values: {
      label: 'name',
      value: 'id',
    },
    response: false,
  };
  dataModel: DataModel = { ...this.model };
  baseURL: string;
  entityId = '';
  createType: string;
  apiEndPoint: string;
  queryParams: Record<string, string>;

  searchAPIEndPoint?: string;
  searchQueryParams?: Record<string, string>;
  searchModel?: DataModel = { ...this.model };

  postAPIEndPoint?: string;
  postQueryParams?: Record<string, string>;
  postModel?: DataModel;

  // Settings variables
  showHeader = true;
  showChips = false;
  maxSelectedLabels = 1;
  _reinitialize = false;

  @Input() settings: MultiSelectSettings;
  @Input() controlName: string;

  @Input() set reinitialize(value: boolean) {
    if (value !== undefined && value !== this._reinitialize) {
      this.resetApiData = true;
      this.offSet = 0;
      this.searchOffset = 0;
      this.getItems();
    }
    this._reinitialize = value;
  }

  @Input() label: string;
  @Input() inputType: 'select' | 'multiselect' = 'select';
  @Input() set paginationConfig(values: PaginationConfig) {
    this.setValues<PaginationConfig>(values);
  }
  @Input() set apiConfig(values: APIConfig) {
    this.setValues<APIConfig>(values);
  }
  @Input() set props(values: QSProps) {
    this.setValues<QSProps>(values);
    this._qsProps = values;
    const selectedOption = values?.selectedOption;
    if (selectedOption) {
      this.pinnedOption = selectedOption;
      this.applyOptions(this.menuOptions);

      this.selectControl?.patchValue(selectedOption.value, {
        emitEvent: false,
      });
    }
  }

  @Input() isPrePatched: boolean = false;
  @Input() showActiveOptionsOnly = false;

  @Input() set initItems(value: boolean) {
    value && this.getItems(this.isPrePatched);
  }

  /**
   * Use this only if you want to use default options
   * and not call get api
   */
  @Input() set defaultOptions(values: Option[]) {
    if (values?.length) {
      this.applyOptions(values);
      this.noMoreData = values.length < this.limit;
    }
  }

  @Output() clickedOption = new EventEmitter<Option>();
  @Output() missingOption = new EventEmitter<Option>();
  @Output() openSidebar = new EventEmitter<boolean>();

  $subscription = new Subscription();

  /**
   * Option of the currently selected value. It is kept outside of @var menuOptions so that
   * it can be re-attached to every page of options, otherwise the input renders a blank label
   * whenever the loaded page does not contain the selected record.
   */
  private pinnedOption: Option = null;

  /** Only the response of the latest request is applied, paginated calls can resolve out of order */
  private requestId = 0;
  private optionsSubscription = new Subscription();
  /** True while any options request is in flight (includes silent pagination loads) */
  private isFetching = false;

  constructor(
    private route: Router,
    private adminUtilityService: AdminUtilityService,
    private apiService: ApiService,
    public controlContainer: ControlContainer,
    private snackbarService: SnackBarService
  ) {
    super(controlContainer);
  }

  ngOnInit(): void {
    this.listenControl();
  }

  /**
   * @function setValues generic method to setting instance value
   * @param values will be values of all props
   * @var modelList for mapping model
   * @var endPoint if prefix '/' representing baseURL, 'URL
   */
  setValues<T extends PropsTypes>(values: T) {
    const modelList = ['dataModel', 'searchModel', 'postModel'];
    const endPointKeys = [
      'apiEndPoint',
      'searchAPIEndPoint',
      'postAPIEndPoint',
    ];
    Object.keys(values).forEach((key) => {
      if (endPointKeys.includes(key)) {
        const endPoint =
          values[key][0] != '/'
            ? `/api/v1/entity/${this.entityId}/${values[key] ?? ''}`
            : values[key];
        this[key] = endPoint;
      } else {
        if (modelList.includes(key)) {
          Object.keys(values[key]).forEach((itemKey) => {
            this[key][itemKey] = values[key][itemKey];
          });
        } else {
          this[key] = values[key];
        }
      }
    });
  }

  get selectControl() {
    return this.controlContainer?.control?.get(this.controlName);
  }

  /**
   * @function applyOptions sets the menu options while keeping @var pinnedOption in the list,
   * so that the selected label is never lost when a page of options replaces the previous one.
   * @param options the options to render
   */
  private applyOptions(options: Option[]) {
    const uniqueOptions = getUniqueOptions(options);

    if (!this.pinnedOption) {
      this.menuOptions = uniqueOptions;
      return;
    }

    const loadedOption = uniqueOptions.find(
      (item) => item?.value === this.pinnedOption.value
    );

    // The loaded record holds the complete data, the pinned one may only carry the label
    if (loadedOption) this.pinnedOption = loadedOption;

    this.menuOptions = loadedOption
      ? uniqueOptions
      : [...uniqueOptions, this.pinnedOption];
  }

  /**
   * @function startRequest cancels the request in flight and returns the id of the new one
   * @param showLoading when false (pagination), skip the spinner so the open panel does not re-render
   */
  private startRequest(showLoading = true) {
    this.optionsSubscription.unsubscribe();
    this.isFetching = true;
    if (showLoading) {
      this.qsLoading = true;
    }
    return ++this.requestId;
  }

  private finishRequest(requestId: number) {
    if (requestId !== this.requestId) return;
    this.isFetching = false;
    this.qsLoading = false;
  }

  /**
   * @function search To search categories
   * @function getOptions modify the response as Option[] type using @var DataModel type
   * @param key search text
   * @param searchOffset offset of the page to load
   */
  search(key: string, searchOffset = 0) {
    if (!key) {
      this.offSet = 0;
      this.searchOffset = 0;
      this.resetApiData = true;
      this.getItems();
      return;
    }

    const apiEndPoint = this.searchAPIEndPoint
      ? this.searchAPIEndPoint
      : this.apiEndPoint;

    this.searchOffset = searchOffset;
    // Only show spinner on a fresh search; keep scroll stable while loading more results
    const requestId = this.startRequest(searchOffset === 0);

    this.optionsSubscription = this.apiService
      .get(
        `${apiEndPoint}${this.getQueryConfig({
          key: encodeURIComponent(key.trim()),
          ...this.queryParams,
          limit: this.limit,
          offset: searchOffset,
        })}`,
        {
          'entity-id': this.entityId,
        }
      )
      .subscribe(
        (res) => {
          if (requestId !== this.requestId) return;
          const model = this.searchModel ? this.searchModel : this.dataModel;
          const data = this.getOptions(res, model);
          this.applyOptions(
            searchOffset === 0 ? data : [...this.menuOptions, ...data]
          );
          this.noMoreData = data.length < this.limit;
        },
        () => this.finishRequest(requestId),
        () => this.finishRequest(requestId)
      );

    this.$subscription.add(this.optionsSubscription);
  }

  /**
   * @function getItems Get Item by API
   * @function getOptions modify the response as Option[] type using @var DataModel type
   * @function removeDuplicate will remove duplicate from the option list
   * @var noMoreData stop pagination when limit will cross
   */
  getItems(isPatchValue: boolean = false) {
    let paginationConfig = !this.isPagination
      ? { pagination: false }
      : { offset: this.offSet, limit: this.limit };

    // Spinner only for first page / reset — pagination must not flicker the open dropdown
    const requestId = this.startRequest(
      this.resetApiData || this.offSet === 0
    );

    this.optionsSubscription = this.apiService
      .get(
        `${this.apiEndPoint}${this.getQueryConfig({
          ...this.queryParams,
          ...paginationConfig,
        })}`,
        {
          'entity-id': this.entityId,
        }
      )
      .subscribe(
        (res) => {
          if (requestId !== this.requestId) return;
          const data = this.getOptions(res, this.dataModel);
          this.applyOptions(
            this.resetApiData ? data : [...this.menuOptions, ...data]
          );

          if (this.showActiveOptionsOnly) {
            // Mutate in place — remapping to new objects forces a full list re-render and scroll jump
            this.menuOptions.forEach((option) => {
              option.color = option.status ? '#1468F9' : '#EF1D45';
            });
          }

          isPatchValue &&
            this.menuOptions.length &&
            this.selectControl?.setValue(this.menuOptions[0].value, {
              emitEvent: true,
            });

          // To be improved later.
          this.emitResponse &&
            this.selectControl?.setValue(this.selectControl?.value, {
              emitEvent: true,
            });
          this.noMoreData = data.length < this.limit;
        },
        () => this.finishRequest(requestId),
        () => this.finishRequest(requestId)
      );

    this.$subscription.add(this.optionsSubscription);
  }

  /**
   *
   * @param res response of api
   * @param model which kind of model is used for api
   * @returns list of option[]
   */
  getOptions(res, model) {
    const items = res
      ? model.response
        ? res
        : model?.key
        ? res[model.key]
        : res
      : [];
    return (
      items.map((item) => {
        const label = Array.isArray(model.values?.label)
          ? `${item[model.values?.label[0]] ?? ''}${
              item[model.values?.label[1]]
                ? ' ' + item[model.values?.label[1]]
                : ''
            }`.trim()
          : this.getNestedPropertyValue(item, model?.values?.label);

        let extras =
          model.values?.extras &&
          (Array.isArray(model.values?.extras)
            ? `${this.getNestedPropertyValue(
                item,
                model?.values?.extras[0]
              )} ${this.getNestedPropertyValue(item, model?.values?.extras[1])}`
            : this.getNestedPropertyValue(item, model?.values?.extras));

        return {
          ...item,
          label: Array.isArray(model.values?.label)
            ? label
            : convertToTitleCase(label ?? ''),
          value: item[model.values.value] ?? '',
          extras: extras ?? '',
        };
      }) ?? []
    );
  }

  getNestedPropertyValue(obj, path) {
    const returnval = path
      .split('.')
      .reduce(
        (acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined),
        obj
      );
    return returnval ?? '';
  }

  /**
   * @function loadMoreData is paginator will set previous offset + 5 and call @function getItems to load data
   */
  loadMoreData(data?: string) {
    if (this.isFetching || this.noMoreData) return;
    this.resetApiData = false;
    if (data) {
      this.offSet = 0;
      this.search(data, this.searchOffset + this.limit);
    } else {
      this.searchOffset = 0;
      this.offSet = this.offSet + this.limit;
      this.getItems();
    }
  }

  /**
   * @function create for prompt link
   * @instance inputPrompt POST API will be call & data will be reload
   */
  create(event) {
    if (this.createType === 'sidebar') {
      this.openSidebar.emit(true);
      return;
    }
    if (event?.length) {
      //Case When inputPrompt haven't to redirect...
      if (this._qsProps.inputPrompt) {
        const model = this.postModel ? this.postModel : this.dataModel;
        // API call with values
        let endPoint = this.postAPIEndPoint
          ? this.postAPIEndPoint
          : this.apiEndPoint;
        endPoint =
          endPoint[0] == '/'
            ? endPoint
            : `/api/v1/entity/${this.entityId}/${endPoint}`;
        const data = {
          ...model?.data,
          [model.key]: event,
        };
        this.$subscription.add(
          this.apiService.post(endPoint, data).subscribe(
            (res) => {
              // push created option
              this.menuOptions.push({
                ...res,
                label: Array.isArray(model.values?.label)
                  ? `${res[model.values?.label[0]]} ${
                      res[model.values?.label[1]]
                    }` // Concatenate labels in case of an array
                  : res[model.values?.label],
                value: res[model.values.value],
              });
              this.controlContainer.control
                .get(this.controlName)
                .setValue(res[model.values.value]);
              this.snackbarService.openSnackBarAsText(
                this.label + ' created successfully',
                '',
                { panelClass: 'success' }
              );
            },
            () => {
              this.offSet = 0;
              this.searchOffset = 0;
              this.resetApiData = true;
              this.getItems();
            }
          )
        );
      }
    } else {
      //The case when you want to redirect
      this.route.navigate([this._qsProps.promptLink]);
    }
  }

  /**
   *
   * @param config configuration of the api calling
   * @returns
   */
  getQueryConfig(config?: Record<string, string | number | boolean>) {
    return this.adminUtilityService.makeQueryParams([config]);
  }

  /**
   * @function listenControl will emit the selected option to parent
   */
  listenControl() {
    this.selectControl?.valueChanges.subscribe((res) => {
      if (!res?.length) {
        this.pinnedOption = null;
        return;
      }
      const selectedOption = this.menuOptions?.find(
        (item) => item.value === res
      );
      if (selectedOption) {
        if (!Array.isArray(res)) this.pinnedOption = selectedOption;
        this.clickedOption.emit(selectedOption);
      } else this.missingOption.emit(res);
    });
  }

  ngOnDestroy() {
    this.$subscription.unsubscribe();
  }
}

type PropsTypes = PaginationConfig | APIConfig | QSProps | MultiSelectSettings;

interface PaginationConfig {
  isPagination: boolean;
  offSet?: number;
  noMoreData?: boolean;
  limit?: number;
}

export type APIConfig = {
  entityId: string;
  apiEndPoint: string;
  dataModel: DataModel;
  queryParams?: Record<string, string>;

  searchAPIEndPoint?: string;
  searchModel?: DataModel;
  searchQueryParams?: Record<string, string>;

  postApiEndPoint?: string;
  postModel?: DataModel;
  postQueryParams?: Record<string, string>;
};

export type QSProps = FormProps & {
  createPrompt: string;
  promptLink: string;
  selectedOption: Option;
  showChips: boolean;
  createType?: string;
  emitResponse?: boolean;
};

/**
 * @type DataModel The key is optional. Without it, the response will be [{}]
 * @type Record<string,string> for the postDataModel
 */
export type DataModel = {
  key?: string;
  values: { label: string | string[]; value: string } | Record<string, string>;
  data?: Record<string, string>;
  response?: boolean;
  extras?: string;
};
